import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Loader, AlertCircle, CheckCircle } from 'lucide-react'
import { oauthService } from '@/services/oauth'

export function OAuthCallback() {
  const { provider } = useParams<{ provider: string }>()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')

  useEffect(() => {
    const handleCallback = async () => {
      if (!provider) {
        setStatus('error')
        setMessage('No OAuth provider specified')
        return
      }

      try {
        setStatus('loading')
        setMessage('Processing OAuth callback...')
        
        await oauthService.handleOAuthCallback(provider, searchParams)
        
        setStatus('success')
        setMessage('Authentication successful! Redirecting...')
        
        // The OAuth service will handle the redirect
      } catch (error: any) {
        setStatus('error')
        setMessage(error.message || 'Authentication failed')
        
        // Redirect to auth page after error
        setTimeout(() => {
          window.location.href = '/auth'
        }, 3000)
      }
    }

    handleCallback()
  }, [provider, searchParams])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          {status === 'loading' && (
            <div className="flex flex-col items-center">
              <Loader className="w-12 h-12 text-purple-600 animate-spin mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing...</h2>
            </div>
          )}
          
          {status === 'success' && (
            <div className="flex flex-col items-center">
              <CheckCircle className="w-12 h-12 text-green-600 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Success!</h2>
            </div>
          )}
          
          {status === 'error' && (
            <div className="flex flex-col items-center">
              <AlertCircle className="w-12 h-12 text-red-600 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Failed</h2>
            </div>
          )}
        </div>
        
        <p className="text-gray-600 mb-6">{message}</p>
        
        {status === 'error' && (
          <button
            onClick={() => window.location.href = '/auth'}
            className="w-full py-2 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Back to Sign In
          </button>
        )}
      </div>
    </div>
  )
}